import { GetServerSideProps, NextPage } from 'next';
import React from 'react';
import styled from 'styled-components';

import { pagesConfigStore } from '~/atomic/atom/links/pagesConfigStore';
import { CommonLayout } from '~/atomic/layout/CommonLayout';
import { LayoutType } from '~/atomic/layout/DefaultLayout';
import { PageTemplate } from '~/atomic/organism/PageTemplate/PageTemplate';
import { TagPage } from '~/atomic/page/catalog/tagPage/TagPage';
import { getBooks } from '~/atomic/page/catalog/tagPage/tagPage.data';
import { isHidePageSelector, tagPageSeoSelector } from '~/atomic/page/catalog/tagPage/tagPage.selector';
import { checkAuthToken } from '~/feature/authorization/checkAuthToken';
import { getAuthTokenFromServer } from '~/feature/authorization/getAuthToken';
import {
  checkVMDParamsInQueries,
  getFilterFromUrl,
} from '~/feature/catalog/filter/catalogFilter.data';
import { catalogFilterSelector } from '~/feature/catalog/filter/catalogFilter.selector';
import { catalogFilterActions } from '~/feature/catalog/filter/catalogFilter.slice';
import { getPagingFromUrl } from '~/feature/pagination/pagination.data';
import { checkSupportWebp } from '~/feature/supportWebp/checkSupportWebp';
import { getTagBySlug } from '~/feature/tag/tag.data';
import { tagSelector } from '~/feature/tag/tag.selector';
import {
  createStore, RootState, useAppSelector,
} from '~/store';

const CatalogTagPage:NextPage & LayoutType = () => {
  const { meta, og } = useAppSelector(tagPageSeoSelector);
  const isHidePage = useAppSelector(isHidePageSelector);

  return (
    <PageTemplate
      isPublic={pagesConfigStore.tag.isPublic}
      seo={{
        title: og.title,
        description: og.description,
        canonical: meta.canonical,
        customSeoTags: isHidePage && (
          <meta name="robots" content="noindex, nofollow" />
        ),
      }}
      content={(
        <SCCatalogPage>
          <TagPage />
        </SCCatalogPage>
      )}
    />
  );
};

CatalogTagPage.getLayout = (page) => (
  <CommonLayout>{ page }</CommonLayout>
);

export const getServerSideProps: GetServerSideProps<
{ state: RootState },
{ keywords?: string[]; }
> = async (context) => {
  try {
    const { store, dispatch } = await createStore();
    await checkAuthToken(context, dispatch);
    const token = getAuthTokenFromServer(context);
    checkSupportWebp(context.req, dispatch);

    dispatch(catalogFilterActions.changePageUrlStartWith(pagesConfigStore.tag.url()));
    dispatch(catalogFilterActions.setIsAddGenreToParams(false));
    await dispatch(getFilterFromUrl(context));
    await dispatch(checkVMDParamsInQueries(context));
    await dispatch(getPagingFromUrl(context));

    const { tagSlug } = catalogFilterSelector(store.getState());
    const normalizedTagSlug = typeof tagSlug === 'string' && tagSlug.length > 0 ? tagSlug : null;

    if (!normalizedTagSlug) {
      return { notFound: true };
    }

    const [booksResult, tagResult] = await Promise.all([
      dispatch(getBooks({ token })),
      dispatch(getTagBySlug({ slug: normalizedTagSlug })),
    ]);

    if (getTagBySlug.rejected.match(tagResult)) {
      return { notFound: true };
    }

    if (getBooks.rejected.match(booksResult)) {
      return { notFound: true };
    }

    const { currentTag } = tagSelector(store.getState());

    if (!currentTag || !('id' in currentTag)) {
      return { notFound: true };
    }

    const state = store.getState();
    return { props: { state } };
  } catch (error) {
    return { notFound: true };
  }
};

const SCCatalogPage = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 12px;
  margin-bottom: 52px;
`;

export default CatalogTagPage;
